import { ArrowUpRight, ShieldCheck, MapPin, Star } from "lucide-react";
import CountUp from "./CountUp";

const HERO_IMG =
    "https://images.unsplash.com/photo-1586191552066-d52dd1e3af86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjYXIlMjB0cmFuc3BvcnQlMjB0cnVja3xlbnwwfHx8fDE3ODAzMDQ2MjV8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] overflow-hidden bg-ink"
        >
            {/* BG image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Camion de convoyage de véhicules Carvaelis"
                    className="w-full h-full object-cover scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(31,111,240,0.25),transparent_50%)]" />
            </div>

            {/* Content */}
            <div className="relative z-10 container-x w-full min-h-[100svh] flex flex-col justify-end pt-28 sm:pt-32 pb-16 sm:pb-24">
                {/* Status pill */}
                <div
                    data-testid="hero-status-pill"
                    className="inline-flex w-fit items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-md px-4 py-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/90 mb-8 sm:mb-10"
                >
                    <span className="relative flex w-2 h-2">
                        <span className="absolute inset-0 bg-signal rounded-full animate-ping opacity-70" />
                        <span className="relative w-2 h-2 bg-signal rounded-full" />
                    </span>
                    Plateforme active · 24/7
                </div>

                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <h1
                            data-testid="hero-title"
                            className="font-display font-black tracking-tighter text-white text-balance text-[2.75rem] sm:text-7xl lg:text-[7.5rem] leading-[0.95]"
                        >
                            Le convoyage
                            <br />
                            <span className="text-signal">de véhicules,</span>
                            <br />
                            réinventé.
                        </h1>
                        <p
                            data-testid="hero-subtitle"
                            className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-xl text-white/70 leading-relaxed"
                        >
                            Carvaelis met en relation particuliers,
                            professionnels et convoyeurs vérifiés pour le
                            transport de véhicules en toute sécurité —
                            partout en France.
                        </p>

                        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <a
                                href="https://carvaelis.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-primary"
                                className="group inline-flex items-center justify-center gap-3 bg-signal hover:bg-signal-dark text-white px-6 sm:px-8 py-4 sm:py-5 font-bold uppercase tracking-wider text-xs sm:text-sm transition-all"
                            >
                                Accéder à la plateforme
                                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                            </a>
                            <a
                                href="#how-it-works"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center justify-center gap-3 border-2 border-white/40 text-white hover:bg-white hover:text-ink px-6 sm:px-8 py-4 sm:py-5 font-bold uppercase tracking-wider text-xs sm:text-sm transition-all"
                            >
                                Comment ça marche
                            </a>
                        </div>
                    </div>

                    {/* Stats column */}
                    <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-px bg-white/10 border-l-0 lg:border-l border-white/10 lg:pl-8 mt-10 lg:mt-0">
                        {[
                            {
                                animValue: 100,
                                suffix: "%",
                                decimals: 0,
                                display: null,
                                v: "Véhicules assurés",
                                icon: ShieldCheck,
                            },
                            {
                                animValue: null,
                                display: "GPS",
                                v: "Suivi temps réel",
                                icon: MapPin,
                            },
                            {
                                animValue: 4.9,
                                suffix: "/5",
                                decimals: 1,
                                display: null,
                                v: "Note moyenne",
                                icon: Star,
                            },
                        ].map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={i}
                                    data-testid={`hero-stat-${i}`}
                                    className="bg-ink/40 backdrop-blur-sm p-4 sm:p-5 lg:p-6"
                                >
                                    <Icon
                                        className="w-5 h-5 text-signal mb-3"
                                        strokeWidth={2}
                                    />
                                    <div className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight">
                                        {s.display ? (
                                            s.display
                                        ) : (
                                            <CountUp
                                                value={s.animValue}
                                                suffix={s.suffix}
                                                decimals={s.decimals}
                                                duration={1800}
                                                testId={`hero-stat-value-${i}`}
                                            />
                                        )}
                                    </div>
                                    <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/60 mt-1">
                                        {s.v}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
